/**
 * Toque de chamada ENTRANTE no painel — Web Audio puro, sem arquivo.
 *
 * Mesmo racional do play-sound das notificações: AudioContext próprio,
 * destravado pelo primeiro user gesture da página (primeAudioUnlock global
 * já roda no AppShell — atendente logado sempre interagiu). Se o contexto
 * estiver suspenso, o toque falha silencioso — o card visual continua.
 */

let ctx: AudioContext | null = null;
let cycleTimer: ReturnType<typeof setInterval> | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx) return ctx;
  // biome-ignore lint/suspicious/noExplicitAny: webkitAudioContext em Safari antigo
  const Ctor = window.AudioContext ?? (window as any).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
    return ctx;
  } catch {
    return null;
  }
}

/** Envelope de trill (campainha): |sin| a ~20Hz durante o toque. */
const TRILL_CURVE = (() => {
  const curve = new Float32Array(2048);
  for (let i = 0; i < curve.length; i += 1) {
    const t = i / curve.length;
    // 22 pulsos ao longo do toque, com fade-in/out nas pontas.
    const pulse = Math.abs(Math.sin(Math.PI * 22 * t));
    const edge = Math.min(1, t / 0.04, (1 - t) / 0.08);
    curve[i] = 0.16 * pulse * Math.max(0, edge);
  }
  return curve;
})();

function chime(audioCtx: AudioContext, at: number): void {
  // Campainha de telefone clássica: par 400+450Hz com trill de ~20Hz por
  // ~1.1s — "trrrrim" inconfundível de ligação chegando.
  const ringDur = 1.1;
  for (const freq of [400, 450]) {
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, at);
    gain.gain.setValueCurveAtTime(TRILL_CURVE, at, ringDur);
    osc.connect(gain).connect(audioCtx.destination);
    osc.start(at);
    osc.stop(at + ringDur + 0.05);
  }
}

export function startRingtone(): void {
  stopRingtone();
  const audioCtx = getCtx();
  if (!audioCtx) return;
  if (audioCtx.state === 'suspended') {
    void audioCtx.resume().catch(() => {});
  }
  const play = (): void => {
    if (audioCtx.state !== 'running') return;
    chime(audioCtx, audioCtx.currentTime);
  };
  play();
  cycleTimer = setInterval(play, 2800);
}

export function stopRingtone(): void {
  if (cycleTimer) clearInterval(cycleTimer);
  cycleTimer = null;
}
